import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { MailCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { RECOVERY_VERIFIED_KEY } from '@/lib/auth-recovery';
import { notifySignupConfirmed } from '@/api/billing';

const searchSchema = z.object({
  token_hash: z.string().optional(),
  type: z
    .enum(['signup', 'recovery', 'invite', 'magiclink', 'email_change', 'email'])
    .optional(),
});

export const Route = createFileRoute('/auth/confirm')({
  component: AuthConfirmPage,
  validateSearch: searchSchema,
});

/**
 * Explicit landing for Supabase auth email links (verify-email, password reset).
 *
 * The email templates link here with `?token_hash=...&type=...` instead of the
 * default {{ .ConfirmationURL }}. That matters for two reasons:
 *  1. The app — not supabase-js's implicit `detectSessionInUrl` — decides what a
 *     link does, keyed on `type`. Previously verify links landed on `/` and were
 *     consumed silently, indistinguishable from a recovery landing.
 *  2. A recovery link only unlocks /reset-password when verifyOtp SUCCEEDS
 *     (per-tab flag) — a failed/expired link can never leave the reset form
 *     usable against whatever session happens to be ambient, which is how a
 *     DIFFERENT signed-in account used to get its password changed.
 *
 * Deliberately NO pre-verify signOut: verifyOtp success replaces the local
 *     session with the link's account anyway, and signing out first meant a
 *     double-clicked link (second tab) revoked the first tab's just-verified
 *     session, and any dead link collaterally logged the user out.
 */
function AuthConfirmPage() {
  const { token_hash, type } = useSearch({ from: '/auth/confirm' });
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    void (async () => {
      if (!token_hash || !type) {
        setError('This link is incomplete. Please use the link from your email, or request a new one.');
        return;
      }

      const { data: verified, error: verifyError } = await supabase.auth.verifyOtp({ type, token_hash });
      if (verifyError) {
        setError('This link is invalid or has expired. Please request a new one.');
        return;
      }

      if (type === 'recovery') {
        // Mark this tab as recovery-verified — /reset-password refuses to
        // change a password without it (or a PASSWORD_RECOVERY auth event).
        sessionStorage.setItem(RECOVERY_VERIFIED_KEY, '1');
        window.location.href = '/reset-password';
      } else {
        // A new email sign-up just confirmed: let GoHighLevel hear about their
        // Free pick now (best effort, capped so a slow call never holds them here).
        const confirmedUser = verified?.user;
        if ((type === 'signup' || type === 'email') && confirmedUser?.email) {
          await Promise.race([
            notifySignupConfirmed(confirmedUser.id, confirmedUser.email).catch(() => {}),
            new Promise((r) => setTimeout(r, 3000)),
          ]);
        }
        window.location.href = '/';
      }
    })();
  }, [token_hash, type]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md border-border bg-card/80 backdrop-blur-sm shadow-xl">
        <CardHeader className="text-center space-y-2 pb-6">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-2">
            <MailCheck className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-3xl font-bold tracking-tight text-foreground">
            {error ? 'Link Problem' : 'Confirming…'}
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            {error ?? 'Verifying your email link. This only takes a moment.'}
          </CardDescription>
        </CardHeader>
        {error && (
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground text-center">
              You can request a new reset link from the sign-in page ("Forgot password?").
            </p>
            <Button className="w-full" onClick={() => navigate({ to: '/login' })}>
              Go to sign in
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
